import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useApi } from '../../hooks/useApi';
import JsonEditor from '../../components/JsonEditor';
import CommandRow from '../EndpointDevice/CommandRow';
import { gapInfo, ruleText, sortedFindings, versionText } from '../EndpointDevice/softwarePolicy';
import { errorText } from '../SoftwarePolicy/policyEdit';

const ACCOUNT_KEY = 'softwareDriftAccount';

const readStored = (key) => {
  try { return sessionStorage.getItem(key); } catch (ex) { return null; }
};
const store = (key, value) => {
  try { sessionStorage.setItem(key, value); } catch (ex) { /* a private window: the choice is just not remembered */ }
};
const copy = (text) => {
  try { navigator.clipboard?.writeText(text); } catch (ex) { console.error('Could not copy:', ex); }
};

export const fleetUrl = (account) => `cma/softwaredriftfleet?${new URLSearchParams({ account }).toString()}`;
export const QUEUE_URL = 'cma/softwaredrift/queue';

// A finding as the queue route is told about it: it names the finding, never the script or its parameters.
export const findingRef = (f) => ({ key: f.key, name: f.name, gap: f.gap });
const selectionId = (f) => `${f.key}|${f.name}|${f.gap}`;
export const hasJob = (f) => Boolean(f?.checkJob && f?.applyJob);

const list = (v) => (Array.isArray(v) ? v : []);

// How many findings of each kind a machine has, worst first: [['forbidden-present', 2], ['outdated', 1]].
export const gapCounts = (machine) => {
  const counts = {};
  list(machine?.findings).forEach((f) => { counts[f.gap] = (counts[f.gap] || 0) + 1; });
  return Object.entries(counts).sort((a, b) => gapInfo(a[0]).rank - gapInfo(b[0]).rank);
};

function MachineRow({ machine, open, onToggle, onQueue }) {
  const findings = sortedFindings(machine.findings);
  const counts = gapCounts(machine);
  const name = machine.computer || machine.deviceId;
  const [selected, setSelected] = useState({});            // selectionId -> true
  const [confirming, setConfirming] = useState(false);     // the apply jobs wait for a yes
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);            // what the queue said, or an error
  const withJobs = findings.filter(hasJob);
  const chosen = withJobs.filter((f) => selected[selectionId(f)]);
  const allChosen = withJobs.length > 0 && chosen.length === withJobs.length;

  const queue = async (kind) => {
    setBusy(true);
    setOutcome(null);
    try {
      const result = await onQueue(machine, kind, chosen.map(findingRef));
      setOutcome({ kind, ...result });
      setSelected({});
    } catch (ex) {
      setOutcome({ kind, error: errorText(ex) });
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };
  return (
    <>
      <TableRow hover sx={{ verticalAlign: 'top' }}>
        <TableCell>{machine.computer || machine.deviceId}</TableCell>
        <TableCell>
          {machine.status === 'error' ? <Chip size="small" color="error" label={machine.message || 'Failed'} /> : (
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
              {counts.length === 0 && <Chip size="small" color="success" variant="outlined" label="In line with the policy" />}
              {counts.map(([gap, n]) => <Tooltip key={gap} title={gapInfo(gap).meaning}><Chip size="small" color={gapInfo(gap).color} label={`${gapInfo(gap).label} ${n}`} /></Tooltip>)}
            </Stack>
          )}
        </TableCell>
        <TableCell>{list(machine.ruleSets).map((r) => r.description || r.filterText).join(', ')}</TableCell>
        <TableCell>{machine.updatedUtc ? machine.updatedUtc.slice(0, 16).replace('T', ' ') : ''}</TableCell>
        <TableCell align="right">
          <Button size="small" onClick={onToggle} disabled={findings.length === 0} aria-label={`${open ? 'Hide' : 'Show'} findings of ${machine.computer || machine.deviceId}`}>
            {open ? 'Hide' : 'Findings'}
          </Button>
        </TableCell>
      </TableRow>
      {open && findings.length > 0 && (
        <TableRow>
          <TableCell colSpan={5} sx={{ bgcolor: 'action.hover' }}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ pb: 1 }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={allChosen} indeterminate={chosen.length > 0 && !allChosen} disabled={withJobs.length === 0 || busy}
                  onChange={(e) => setSelected(e.target.checked ? Object.fromEntries(withJobs.map((f) => [selectionId(f), true])) : {})} />}
                label={`Select all with a script (${withJobs.length})`}
              />
              <Button size="small" variant="outlined" disabled={chosen.length === 0 || busy} onClick={() => queue('check')}>{`Insert check jobs (${chosen.length})`}</Button>
              <Button size="small" variant="contained" color="warning" disabled={chosen.length === 0 || busy} onClick={() => setConfirming(true)}>{`Insert apply jobs (${chosen.length})`}</Button>
              {busy && <CircularProgress size={18} />}
            </Stack>
            {outcome?.error && <Alert severity="error" sx={{ mb: 1 }}>{outcome.error}</Alert>}
            {outcome && !outcome.error && (
              <Alert severity={list(outcome.refused).length || list(outcome.failed).length ? 'warning' : 'success'} sx={{ mb: 1 }} onClose={() => setOutcome(null)}>
                {`${list(outcome.queued).length} ${outcome.kind} ${list(outcome.queued).length === 1 ? 'job' : 'jobs'} in the queue of ${name}.`}
                {list(outcome.queued).map((q) => <Typography key={q.id} variant="caption" display="block">{`${q.name}: ${q.id}`}</Typography>)}
                {list(outcome.refused).map((r) => <Typography key={`r${r.name}`} variant="caption" display="block">{`Not queued, ${r.name}: ${r.error}`}</Typography>)}
                {list(outcome.failed).map((r) => <Typography key={`f${r.name}`} variant="caption" display="block">{`Failed, ${r.name}: ${r.error}`}</Typography>)}
              </Alert>
            )}
            <Table size="small" aria-label={`Findings of ${machine.computer || machine.deviceId}`}>
              <TableBody>
                {findings.map((f, i) => (
                  <TableRow key={`${f.key}-${i}`}>
                    <TableCell padding="checkbox">
                      <Tooltip title={hasJob(f) ? '' : 'No script can do this yet.'}>
                        <span>
                          <Checkbox size="small" disabled={!hasJob(f) || busy} checked={Boolean(selected[selectionId(f)])} inputProps={{ 'aria-label': `Select ${f.name || f.key}` }}
                            onChange={(e) => setSelected({ ...selected, [selectionId(f)]: e.target.checked })} />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell><Chip size="small" color={gapInfo(f.gap).color} label={gapInfo(f.gap).label} /></TableCell>
                    <TableCell>{f.name || f.key}</TableCell>
                    <TableCell>{versionText(f)}</TableCell>
                    <TableCell>{ruleText(f)}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        {f.note && <Typography variant="body2">{f.note}</Typography>}
                        {f.proposedCheck && <CommandRow label="Check" command={f.proposedCheck} />}
                        {f.proposedApply && <CommandRow label="Apply" command={f.proposedApply} />}
                        {!f.proposedCheck && ['outdated', 'missing', 'forbidden-present'].includes(f.gap) && (
                          <Typography variant="caption" color="text.secondary">No script can do this yet.</Typography>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableCell>
        </TableRow>
      )}
      {confirming && (
        <Dialog open onClose={busy ? undefined : () => setConfirming(false)} aria-label="Insert apply jobs">
          <DialogTitle>Insert apply jobs for {name}?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {`${chosen.length} ${chosen.length === 1 ? 'job' : 'jobs'} will be put in the queue of this machine and run for real: they install, update or remove software.`}
            </Typography>
            {chosen.map((f) => <Typography key={selectionId(f)} variant="body2">{`${gapInfo(f.gap).label}: ${f.name || f.key}`}</Typography>)}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirming(false)} disabled={busy}>Cancel</Button>
            <Button color="warning" variant="contained" onClick={() => queue('apply')} disabled={busy}>Insert apply jobs</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

// The software drift of every machine of one account: each machine's latest installed-programs result against the account's rule sets,
// with the whole answer available as JSON.
function SoftwareDriftPage() {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const theme = useTheme();
  const [params, setParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState(null);
  const [accountsError, setAccountsError] = useState('');
  const [report, setReport] = useState({ state: 'idle' });
  const [open, setOpen] = useState({});
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiRef.current.request('GET', 'cma/account')
      .then((response) => {
        if (cancelled) return;
        const sorted = (response.data || []).slice().sort((a, b) => a.code.localeCompare(b.code));
        setAccounts(sorted);
        const wanted = params.get('account') || readStored(ACCOUNT_KEY);
        setAccount(sorted.find((a) => a.code === wanted) || sorted[0] || null);
      })
      .catch((ex) => { if (!cancelled) { console.error('Failed to load accounts:', ex); setAccountsError(errorText(ex)); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async (code) => {
    if (!code) return;
    setReport({ state: 'loading' });
    setOpen({});
    try {
      const response = await apiRef.current.request('GET', fleetUrl(code));
      setReport({ state: 'ok', body: response.data });
    } catch (ex) {
      console.error('Failed to load the software drift report:', ex);
      setReport({ state: 'error', error: errorText(ex) });
    }
  }, []);

  useEffect(() => { load(account?.code); }, [account, load]);

  // Put the jobs of some findings of one machine in its queue. The API takes them from its own drift result: only names go over.
  const queueJobs = useCallback(async (machine, kind, findings) => {
    const response = await apiRef.current.request('POST', QUEUE_URL, { account: account.code, deviceId: machine.deviceId, kind, findings });
    return response.data;
  }, [account]);

  const chooseAccount = (value) => {
    setAccount(value);
    if (value) store(ACCOUNT_KEY, value.code);
    const next = new URLSearchParams(params);
    next.delete('account');
    setParams(next, { replace: true });
  };

  const body = report.state === 'ok' ? report.body || {} : {};
  const machines = list(body.machines);
  const summary = body.summary || {};
  const json = report.state === 'ok' ? JSON.stringify(body, null, 2) : '';

  return (
    <Box sx={{ minHeight: '100vh', pb: 6 }}>
      <Container maxWidth="xl">
        <Box sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, background: alpha(theme.palette.background.paper, 0.6),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
              <HomeRoundedIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
            </Link>
            <Typography variant="body2" color="text.secondary">/</Typography>
            <Link to="/endpointdevices" style={{ textDecoration: 'none' }}>
              <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>Endpoint Devices</Typography>
            </Link>
            <Typography variant="body2" color="text.secondary">/</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Software drift</Typography>
          </Box>
        </Box>

        <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Software drift</Typography>
              <Typography variant="body2" color="text.secondary">
                Every machine of an account against its software policy, from the latest installed-programs result of each machine.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Autocomplete
                size="small" sx={{ minWidth: 260 }} options={accounts} value={account} disableClearable
                getOptionLabel={(a) => (a.name ? `${a.code} - ${a.name}` : a.code)} isOptionEqualToValue={(a, b) => a.code === b.code}
                onChange={(e, value) => chooseAccount(value)} renderInput={(p) => <TextField {...p} label="Account" />}
              />
              <Tooltip title="Reload"><span><IconButton onClick={() => load(account?.code)} disabled={!account || report.state === 'loading'} size="small" aria-label="Reload"><RefreshIcon /></IconButton></span></Tooltip>
              <Button component={Link} to={`/softwarepolicy${account ? `?account=${encodeURIComponent(account.code)}` : ''}`}>Software policy</Button>
              <Button component={Link} to="/endpointdevices" startIcon={<ArrowBackIcon />}>Endpoint Devices</Button>
            </Stack>
          </Stack>
        </Paper>

        {accountsError && <Alert severity="error" sx={{ mb: 2 }}>{accountsError}</Alert>}

        <Paper sx={{ borderRadius: 2, p: 2 }}>
          {(!account && !accountsError) || report.state === 'loading' ? <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack> : null}
          {report.state === 'error' && <Alert severity="error">{report.error}</Alert>}
          {report.state === 'ok' && (
            <Stack spacing={2}>
              {body.policy?.status === 'none' && <Alert severity="info">This account has no software policy yet, so there is nothing to compare with.</Alert>}
              {body.dataComplete === false && <Alert severity="warning">Not every result could be read from VES, so some machines may be missing.</Alert>}
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                <Chip size="small" label={`${summary.machines || 0} machines`} />
                <Chip size="small" color={summary.withFindings ? 'warning' : 'success'} variant="outlined" label={`${summary.withFindings || 0} with findings`} />
                <Chip size="small" variant="outlined" label={`${summary.findings || 0} findings`} />
                {list(body.machinesWithoutResult).length > 0 && (
                  <Tooltip title={body.machinesWithoutResult.join(', ')}><Chip size="small" variant="outlined" label={`${body.machinesWithoutResult.length} have not reported`} /></Tooltip>
                )}
              </Stack>

              {machines.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No machine of this account has reported its installed programs.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" aria-label="Software drift by machine">
                    <TableHead>
                      <TableRow>{['Machine', 'Findings', 'Rule sets that apply', 'Reported', ''].map((h) => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow>
                    </TableHead>
                    <TableBody>
                      {machines.map((m) => (
                        <MachineRow key={m.deviceId || m.computer} machine={m} open={Boolean(open[m.deviceId || m.computer])} onQueue={queueJobs}
                          onToggle={() => setOpen({ ...open, [m.deviceId || m.computer]: !open[m.deviceId || m.computer] })} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button size="small" onClick={() => setShowRaw(!showRaw)}>{showRaw ? 'Hide raw JSON' : 'Raw JSON'}</Button>
                  {showRaw && <Tooltip title="Copy the JSON"><IconButton size="small" aria-label="Copy raw JSON" onClick={() => copy(json)}><ContentCopyIcon fontSize="inherit" /></IconButton></Tooltip>}
                </Stack>
                {showRaw && (
                  <Box sx={{ mt: 1 }}><JsonEditor value={json} label="Raw JSON" maxHeight={700} /></Box>
                )}
              </Box>
            </Stack>
          )}
        </Paper>
      </Container>
    </Box>
  );
}

export default SoftwareDriftPage;
